import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { DndContext, useSensor, useSensors, MouseSensor, TouchSensor, KeyboardSensor, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { IconCirclePlus, IconGripVertical, IconTemplate, IconTrashFilled, IconUpload, IconX } from "@tabler/icons-react";
import { toast } from "sonner";
import { loadingHide, loadingShow } from "../../../loadingUi/loading.controller";
import { uploadImage, uploadWhatsappTemplateImage, uploadWhatsappTemplateVideo } from "../../../api/api.global";
import { postApi } from "../../../api/call.api";
import { urlApi } from "../../../api/url.api";
import IconButton from "../../../components/Buttons/IconButton";

const CreateTemplateModal = ({ orgID, callback, orgName }) => {
    const [show, setShow] = useState(false);
    const [templateName, setTemplateName] = useState("");
    const [selectedLang, setSelectedLang] = useState("");
    const [templateType, setTemplateType] = useState("button");
    const [buttons, setButtons] = useState([]);
    const [selectedMediaType, setSelectedMediaType] = useState("NONE");
    const [mediaFile, setMediaFile] = useState(null);
    const [headerText, setHeaderText] = useState("");
    const [bodyText, setBodyText] = useState("");
    const [footerText, setFooterText] = useState("");
    const [keyword, setKeyword] = useState("");

    const handleClose = () => {
        setTemplateName("");
        setSelectedLang("");
        setTemplateType("button");
        setButtons([]);
        setSelectedMediaType("NONE");
        setMediaFile(null);
        setHeaderText("");
        setBodyText("");
        setFooterText("");
        setKeyword("");
        setShow(false);
    };

    const handleShow = () => setShow(true);

    // DnD Sensors
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor), useSensor(KeyboardSensor));

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const local = [...buttons];
            const activeIndex = local.findIndex((item) => item.sortId === active.id);
            const overIndex = local.findIndex((item) => item.sortId === over.id);
            setButtons(arrayMove(local, activeIndex, overIndex));
        }
    };

    const handleDeleteDraggableItem = (itemId) => setButtons(buttons.filter((b) => b.sortId !== itemId));

    const addButton = () => {
        if (buttons.length >= 3) {
            toast.error("You can add up to 3 buttons only.");
            return;
        }
        setButtons([...buttons, { sortId: Date.now(), text: "", type: "reply" }]);
    };


    const handleMediaUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setMediaFile(file);
    };

    const mediaUrl = useMemo(() => (mediaFile ? URL.createObjectURL(mediaFile) : null), [mediaFile]);
    useEffect(() => () => { if (mediaUrl) URL.revokeObjectURL(mediaUrl); }, [mediaUrl]);

    const submitTemplate = async () => {
        if (!templateName) return toast.error("Template Name is required.");
        if (!bodyText) return toast.error("Body Text is required.");

        console.log("working");

        let postData = {
            name: templateName.trim(),
            organizationId: orgID,
            interactive: {
                type: templateType,
                header: {},
                body: { text: bodyText.trim() },
                action: {},
            },
        };

        if (footerText.trim()) postData.interactive.footer = { text: footerText.trim() };

        if (selectedMediaType === "IMAGE" && mediaFile) {
            try {
                loadingShow();
                const uploadedLink = await uploadWhatsappTemplateImage(mediaFile);
                loadingHide();

                if (!uploadedLink) {
                    toast.error("Failed to upload image. Please try again.");
                    return;
                }

                postData.interactive.header = {
                    type: "image",
                    image: { link: uploadedLink },
                };
            } catch (error) {
                loadingHide();
                toast.error("Failed to upload image. Please try again.");
                console.error("Image upload error:", error);
                return;
            }
        } else if (selectedMediaType === "VIDEO" && mediaFile) {
            try {
                loadingShow();
                const uploadedLink = await uploadWhatsappTemplateVideo(mediaFile);
                loadingHide();

                if (!uploadedLink) {
                    toast.error("Failed to upload video. Please try again.");
                    return;
                }

                postData.interactive.header = {
                    type: "video",
                    video: { link: uploadedLink },
                };
            } catch (error) {
                loadingHide();
                toast.error("Failed to upload video. Please try again.");
                console.error("Video upload error:", error);
                return;
            }
        } else if (headerText.trim()) {
            postData.interactive.header = {
                type: "text",
                text: headerText.trim(),
            };
        }

        // Action handling
        if (templateType === "button") {
            postData.interactive.action.buttons = buttons.map((b) => ({
                type: "reply",
                reply: {
                    id: b.text.toLowerCase().replace(/\s+/g, '-') + "-btn",
                    title: b.text,
                },
            }));
        } else if (templateType === "cta_url") {
            postData.interactive.action = {
                name: "cta_url",
                parameters: {
                    display_text: buttons[0]?.text || "CTA",
                    url: buttons[0]?.url || "#",
                },
            };
        }

        console.log(postData);
        try {
            loadingShow();
            const resp = await postApi(postData, urlApi.addWhatsAppTemplate);
            loadingHide();

            if (resp.responseCode === 200) {
                toast.success(resp.message);
                callback?.();
                handleClose();
            } else {
                toast.error(resp.message);
            }
        } catch (error) {
            loadingHide();
            toast.error("Failed to create template. Please try again.");
            console.error("Template creation error:", error);
        }
    };

    return (
        <>
            <div className="max-w-max" onClick={handleShow}>{<IconTemplate />}</div>
            <Modal show={show} onHide={handleClose} backdrop="static" size="xl">
                <Modal.Body className="rounded-xl p-4">
                    <div className="flex justify-between items-center">
                        <h5 className="font-semibold text-lg">Add New Template</h5>
                        <IconX className="cursor-pointer hover:text-red-500 transition" onClick={handleClose} />
                    </div>
                    <hr className="my-3" />

                    <div className="flex gap-6">
                        {/* Left Column */}

                        <div className="w-2/3 space-y-4 max-h-[78vh] overflow-y-auto">
                            {/* Template Type */}
                            <div>
                                <label className="text-sm font-semibold mb-1 block">Template Type</label>
                                <select
                                    className="w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    value={templateType}
                                    onChange={(e) => setTemplateType(e.target.value)}
                                >
                                    <option value="button">Button Template</option>
                                    <option value="cta_url">CTA URL Template</option>
                                </select>
                            </div>
                            {/* Name */}
                            <div>
                                <label className="text-sm font-semibold mb-1 block">Name</label>
                                <input
                                    type="text"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    placeholder="Enter Template Name"
                                />
                            </div>

                            {/* Body */}
                            <div>
                                <label className="text-sm font-semibold mb-1 block">Message</label>
                                <textarea
                                    value={bodyText}
                                    onChange={(e) => setBodyText(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    placeholder="Enter template body message..."
                                    rows={4}
                                />
                            </div>

                            {/* Media */}
                            <div>
                                <label className="text-sm font-semibold mb-1 block">Media Sample (Optional)</label>
                                <select
                                    className="w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    value={selectedMediaType}
                                    onChange={(e) => { setSelectedMediaType(e.target.value); setMediaFile(null); setHeaderText(""); }}
                                >
                                    <option value="NONE">None</option>
                                    <option value="IMAGE">Image</option>
                                    <option value="VIDEO">Video</option>
                                </select>
                            </div>

                            {selectedMediaType !== "NONE" && (
                                <div>
                                    <label className="text-sm font-semibold mb-1 block">Upload {selectedMediaType}</label>
                                    {!mediaFile ? (
                                        <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-orange-400 transition">
                                            <input type="file" accept={selectedMediaType === "IMAGE" ? ".png,.jpg,.jpeg" : "video/*"} className="hidden" onChange={handleMediaUpload} />
                                            <IconUpload size={32} className="text-gray-400 mb-2" />
                                            <p className="text-sm text-gray-500">Click to upload</p>
                                        </label>
                                    ) : (
                                        <div className="relative w-max mx-auto">
                                            {selectedMediaType === "IMAGE" ? <img src={mediaUrl} alt="preview" className="max-h-48 mx-auto rounded-lg border" /> : <video src={mediaUrl} controls className="w-full max-h-64 rounded-lg border" />}
                                            <button className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full shadow-md hover:bg-red-700 transition" onClick={() => setMediaFile(null)}>
                                                <IconX size={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Header */}
                            <div>
                                <label className="text-sm font-semibold mb-1 block">Header</label>
                                <input
                                    type="text"
                                    value={headerText}
                                    onChange={(e) => setHeaderText(e.target.value)}
                                    placeholder="Enter a simple header text"
                                    disabled={selectedMediaType !== "NONE"}
                                    className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                />
                            </div>

                            {/* Footer */}
                            <div>
                                <label className="text-sm font-semibold mb-1 block">Footer</label>
                                <input
                                    type="text"
                                    value={footerText}
                                    onChange={(e) => setFooterText(e.target.value)}
                                    placeholder="Enter a simple footer text"
                                    className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                />
                            </div>

                            {/* Buttons */}
                            {templateType === "button" && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-semibold">Buttons</label>
                                        <IconButton icon={<IconCirclePlus size={16} />} text="Add Button" onClickFunction={addButton} />
                                    </div>
                                    {buttons.length > 0 && (
                                        <DndContext onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]} collisionDetection={closestCenter} sensors={sensors}>
                                            <SortableContext items={buttons.map((item) => item.sortId)} strategy={verticalListSortingStrategy}>
                                                {buttons.map((item) => (
                                                    <DraggableButton key={item.sortId} buttonData={item} buttons={buttons} setButtons={setButtons} handleDeleteDraggableItem={handleDeleteDraggableItem} />
                                                ))}
                                            </SortableContext>
                                        </DndContext>
                                    )}
                                </div>
                            )}

                            {templateType === "cta_url" && (
                                <div>
                                    <label className="text-sm font-semibold mb-1 block">CTA Text & URL</label>
                                    <input type="text" placeholder="Button Text" value={buttons[0]?.text || ""} onChange={(e) => setButtons([{ sortId: Date.now(), text: e.target.value, type: "URL", url: buttons[0]?.url || "" }])} className="w-full px-3 py-2 border rounded-md mb-2" />
                                    <input type="text" placeholder="Button URL" value={buttons[0]?.url || ""} onChange={(e) => setButtons([{ sortId: Date.now(), text: buttons[0]?.text || "", type: "URL", url: e.target.value }])} className="w-full px-3 py-2 border rounded-md" />
                                </div>
                            )}
                        </div>

                        {/* Right Column: Preview */}
                        <div className="w-1/3">
                            <h6 className="font-semibold mb-2">Preview</h6>
                            <div className="relative w-[320px] h-[640px] mx-auto border-8 border-black rounded-[2rem] shadow-xl overflow-hidden">
                                <div className="bg-[#075e54] text-white px-4 py-2 flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-gray-300"></div>
                                    <div className="flex-1">
                                        <div className="font-semibold text-sm">Contact Name</div>
                                        <div className="text-xs text-gray-200">online</div>
                                    </div>
                                    <div className="text-white text-lg">⋮</div>
                                </div>

                                <div className="bg-[#ece5dd] p-3 h-[500px] overflow-y-auto">
                                    <div className="flex justify-end">
                                        <div className="bg-[#dcf8c6] rounded-lg p-3 shadow text-sm max-w-[80%]">
                                            {headerText && <div className="font-semibold mb-2">{headerText}</div>}
                                            {selectedMediaType === "IMAGE" && mediaFile && <img src={mediaUrl} alt="preview" className="mb-2 rounded-lg max-h-64 max-w-full w-full object-cover" />}
                                            {selectedMediaType === "VIDEO" && mediaFile && <video src={mediaUrl} controls className="mb-2 rounded-lg max-h-64" />}
                                            <div className="mb-2 whitespace-pre-wrap">{bodyText || "Your message will appear here"}</div>
                                            {footerText && <div className="text-xs text-gray-500 mt-2">{footerText}</div>}

                                            {templateType === "button" && buttons.length > 0 && (
                                                <div className="mt-3 flex flex-col gap-1">
                                                    {buttons.map((btn, idx) => (
                                                        <button key={idx} className="w-full text-center py-2 rounded-full border text-[#128C7E] font-medium bg-white hover:bg-gray-50 text-xs">
                                                            {btn.text || "Button"}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            {templateType === "cta_url" && buttons[0] && (
                                                <button className="mt-3 w-full text-center py-2 rounded-full border text-[#128C7E] font-medium bg-white hover:bg-gray-50 text-xs">{buttons[0].text || "CTA"}</button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white border-t px-3 py-2 flex items-center gap-2">
                                    <input type="text" placeholder="Type a message" disabled className="flex-1 px-3 py-2 rounded-full bg-gray-100 text-sm outline-none" />
                                    <div className="text-green-600">🎤</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer Buttons */}
                    <hr className="my-4" />
                    <div className="flex justify-end gap-3">
                        <button className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition" onClick={handleClose}>Cancel</button>
                        <button className="px-4 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition" onClick={submitTemplate}>Add Template</button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default CreateTemplateModal;

// Draggable Button Component
export const DraggableButton = ({ buttonData, buttons, setButtons, handleDeleteDraggableItem }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: buttonData.sortId });
    const style = { transform: CSS.Translate.toString(transform), transition };

    return (
        <div ref={setNodeRef} style={style}>
            <div className="flex items-center gap-2 mb-2 p-2 bg-gray-50 rounded-lg border">
                <button className="cursor-grab text-gray-500" {...attributes} {...listeners}>
                    <IconGripVertical />
                </button>

                <input
                    type="text"
                    value={buttonData.text}
                    onChange={(e) => setButtons(buttons.map(b => b.sortId === buttonData.sortId ? { ...b, text: e.target.value } : b))}
                    placeholder="Button Text"
                    className="flex-1 border h-9 px-2 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                />

                <button onClick={() => handleDeleteDraggableItem(buttonData.sortId)} className="text-red-600 hover:text-red-800 transition">
                    <IconTrashFilled size={18} />
                </button>
            </div>
        </div>
    );
};

